import { useTable } from '@tanstack/react-table';
import {
  type CellData,
  type RowData,
  type TableFeatures,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { forwardRef, type Ref } from 'react';

import { simpleTableFeatures } from './features.js';
import { useColumns } from './hooks/useColumns.js';
import type {
  SimpleTableColumnDefBase,
  SimpleTableProps,
} from './public.api.js';
import * as styles from './SimpleTable.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';

const COMPONENT_NAME = 'SimpleTable';

// https://tanstack.com/table/latest/docs/api/core/column-def#meta
declare module '@tanstack/react-table' {
  interface ColumnMeta<
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TFeatures extends TableFeatures,
    TData extends RowData,
    TValue extends CellData = CellData,
  > {
    alignment?: SimpleTableColumnDefBase<TData, TValue>['alignment'];
    width?: SimpleTableColumnDefBase<TData, TValue>['width'];
    minWidth?: SimpleTableColumnDefBase<TData, TValue>['minWidth'];
    maxWidth?: SimpleTableColumnDefBase<TData, TValue>['maxWidth'];
  }
}

/**
 * The `SimpleTable` component is a simplified version of the `DataTable`. It is
 * designed to handle small sets of data and markdown representation. It does not
 * feature sorting, resizing, filtering, or virtualization and behaves like a
 * native HTML table.
 */
function InternalSimpleTable<TData, TValue>(
  props: SimpleTableProps<TData>,
  forwardedRef: Ref<HTMLTableElement>,
) {
  const {
    data,
    columns,
    variant,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  // Hook used to adapt column definition for internal usage
  const extractedColumns = useColumns<TData, TValue>(columns);

  const table = useTable({
    _features: simpleTableFeatures,
    _rowModels: {},
    data: data as RowData[],
    columns: extractedColumns,
  });

  const combinedVariants: Required<SimpleTableProps['variant']> = {
    rowSeparation: variant?.rowSeparation ?? 'horizontalDividers',
    verticalDividers: variant?.verticalDividers ?? false,
    contained: variant?.contained ?? true,
    rowDensity: variant?.rowDensity ?? 'default',
    fontStyle: variant?.fontStyle ?? 'text',
  };

  return (
    <table
      ref={forwardedRef}
      className={clsx(styles.simpleTable, consumerClassName, {
        'strato-table-contained': combinedVariants.contained,
        'strato-table-colsep-divider': combinedVariants.verticalDividers,
        'strato-table-code-font': combinedVariants.fontStyle === 'code',
        'strato-table-cell-density-default':
          combinedVariants.rowDensity === 'default',
        'strato-table-cell-density-condensed':
          combinedVariants.rowDensity === 'condensed',
        'strato-table-cell-density-comfortable':
          combinedVariants.rowDensity === 'comfortable',
        'strato-table-rowsep-zebra':
          combinedVariants.rowSeparation === 'zebraStripes',
        'strato-table-rowsep-divider':
          combinedVariants.rowSeparation === 'horizontalDividers' ||
          combinedVariants.rowSeparation === 'zebraStripes',
      })}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="strato-table-header">
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={clsx('strato-table-header-cell', {
                  'strato-table-cell-alignment-left':
                    header.column.columnDef.meta?.alignment === 'left',
                  'strato-table-cell-alignment-center':
                    header.column.columnDef.meta?.alignment === 'center',
                  'strato-table-cell-alignment-right':
                    header.column.columnDef.meta?.alignment === 'right',
                  'strato-table-cell-alignment-top-left':
                    header.column.columnDef.meta?.alignment === 'top-left',
                  'strato-table-cell-alignment-top-center':
                    header.column.columnDef.meta?.alignment === 'top-center',
                  'strato-table-cell-alignment-top-right':
                    header.column.columnDef.meta?.alignment === 'top-right',
                  'strato-table-cell-alignment-bottom-left':
                    header.column.columnDef.meta?.alignment === 'bottom-left',
                  'strato-table-cell-alignment-bottom-center':
                    header.column.columnDef.meta?.alignment === 'bottom-center',
                  'strato-table-cell-alignment-bottom-right':
                    header.column.columnDef.meta?.alignment === 'bottom-right',
                })}
                style={{
                  minWidth: header.column.columnDef.meta?.minWidth,
                  width: header.column.columnDef.meta?.width,
                  maxWidth: header.column.columnDef.meta?.maxWidth,
                }}
              >
                {header.isPlaceholder ? null : (
                  <table.FlexRender header={header} />
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => (
          <tr
            key={row.id}
            className={clsx('strato-table-row', {
              'strato-table-row-even': rowIndex % 2,
            })}
          >
            {row.getAllCells().map((cell) => (
              <td
                key={cell.id}
                className={clsx('strato-table-cell', {
                  'strato-table-cell-alignment-left':
                    cell.column.columnDef.meta?.alignment === 'left',
                  'strato-table-cell-alignment-center':
                    cell.column.columnDef.meta?.alignment === 'center',
                  'strato-table-cell-alignment-right':
                    cell.column.columnDef.meta?.alignment === 'right',
                  'strato-table-cell-alignment-top-left':
                    cell.column.columnDef.meta?.alignment === 'top-left',
                  'strato-table-cell-alignment-top-center':
                    cell.column.columnDef.meta?.alignment === 'top-center',
                  'strato-table-cell-alignment-top-right':
                    cell.column.columnDef.meta?.alignment === 'top-right',
                  'strato-table-cell-alignment-bottom-left':
                    cell.column.columnDef.meta?.alignment === 'bottom-left',
                  'strato-table-cell-alignment-bottom-center':
                    cell.column.columnDef.meta?.alignment === 'bottom-center',
                  'strato-table-cell-alignment-bottom-right':
                    cell.column.columnDef.meta?.alignment === 'bottom-right',
                })}
                style={{
                  minWidth: cell.column.columnDef.meta?.minWidth,
                  width: cell.column.columnDef.meta?.width,
                  maxWidth: cell.column.columnDef.meta?.maxWidth,
                }}
              >
                <table.FlexRender cell={cell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
InternalSimpleTable.displayName = COMPONENT_NAME;

/**
 * The `SimpleTable` component is a simplified version of the `DataTable`. It is
 * designed to handle small sets of data and markdown representation. It does not
 * feature sorting, resizing, filtering, or virtualization and behaves like a
 * native HTML table.
 * @public
 */
export const SimpleTable = /* @__PURE__ */ forwardRef(InternalSimpleTable);
